import { ReactNode } from 'react'
import { QueryClient, QueryClientProvider, QueryCache, isServer } from '@tanstack/react-query'
import { TamaguiProvider } from '@tamagui/core'
import { ThemeProvider } from '@fe-toolkit/web-components/ssr'
import tamaguiConfig from '@giffgaff/fe-design-system-ui/config'
import { createLogger } from '@fe-toolkit/observability/client'
import { ClientTrackingContextProvider } from '@fe-toolkit/utility/client/tracking-context'

import { RuntimeConfig, basePath } from '@/config'
import { LINKED_CAMPAIGN_PROPERTIES } from '@/config/campaignProperties'
import { customTheme } from '@/config/customTheme'

const logger = createLogger({ name: 'QueryClient' })

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Hash in the message, not as a field: observability's `LogData` only accepts the
        // keys the MFE log pipeline allowlists, and it drops any record carrying another
        // attribute. `queryHash` is the serialised `queryKey`, so one covers both.
        logger.error(`Query failed: ${query.queryHash}`, { error })
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    if (!browserQueryClient) browserQueryClient = makeQueryClient()

    return browserQueryClient
  }
}

type ProvidersProps = {
  children: ReactNode
  runtimeConfig: RuntimeConfig
  memberId?: number
}

export default function Providers({ children, runtimeConfig, memberId }: ProvidersProps) {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <ClientTrackingContextProvider
        config={{
          baseUrl: runtimeConfig.API_GATEWAY_URL,
          memberId,
          mfeBasePath: basePath,
          withDebug: process.env.NODE_ENV === 'development' || runtimeConfig.ENABLE_CTC_DEBUG_TOOLS === 'true',
          // Without this the mode defaults to 'wait', which makes initialisation await the
          // OneTrust banner for up to cookieConsentTimeoutMs (60s by default) before the CTC
          // call fires, and every caller of getFeatures blocks for that whole window. On a
          // landing page most visitors have not touched the banner, so useFeatures would sit
          // loading for a minute. 'proceed-as-essential' resolves immediately at ESSENTIAL,
          // then reconciles in the background and re-emits when the visitor elevates to FULL.
          //
          // Client-side only. The server-side gate in src/routes/layout.data.ts is resolved by
          // createCtcFeaturesMiddlewareMJS, which never consults consent state, so this
          // setting has no bearing on SSR latency. Coordinated with FAAS-787, which needs the
          // same value.
          preConsent: 'proceed-as-essential',
          // FAAS-787. Portal's campaign parameters reach Portal's own session through the
          // __ggUtmz bridge (server/ggUtmzCookie.ts), but nothing put them on the CTC
          // session, so anything reporting off CTC could not attribute a paid or affiliate
          // landing. See src/config/campaignProperties.ts for the grouping and for why we
          // store raw values rather than Portal's derived campaign names.
          linkedCampaignProperties: LINKED_CAMPAIGN_PROPERTIES,
          // Snapshots the landing query string and referrer once, in memory, so attribution
          // survives SPA navigation, redirects and the consent gate. This page is the paid
          // and affiliate landing surface, so it is the one that needs it.
          //
          // It matters specifically because of preConsent above: the landing context is only
          // sent post-consent, and with 'proceed-as-essential' the first call goes out
          // immediately at ESSENTIAL and the consented call follows later. By then the URL
          // has usually lost its parameters. Without this snapshot that later call carries
          // nothing.
          preserveLandingContext: true,
        }}
      >
        {/*
          Tamagui (@giffgaff/fe-design-system-ui) and the legacy styled-components
          ThemeProvider (@fe-toolkit/web-components) run side by side during the
          migration. TamaguiProvider wraps the legacy ThemeProvider so both
          component sets render anywhere in the tree.

          This must stay the only createTamagui() config active in this process —
          see src/tamagui.config.ts for why a second one (e.g. @fe-toolkit/ui's)
          would silently corrupt this one via Tamagui's global config singleton.
        */}
        <TamaguiProvider config={tamaguiConfig} defaultTheme="light">
          <ThemeProvider customTheme={customTheme}>{children}</ThemeProvider>
        </TamaguiProvider>
      </ClientTrackingContextProvider>
    </QueryClientProvider>
  )
}
